import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AddReviewParams, ProductRating, ReviewsInfo } from '@reviews/const';
import { UserAgentService } from '@shared/services';
import { StateUtils } from '@spartacus/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import * as ReviewsActions from '../store/actions/reviews.action';
import { StateWithReviews } from '../store/reviews-state';
import * as ReviewsSelectors from '../store/selectors/reviews.selectors';

@Injectable({
  providedIn: 'root',
})
export class ProductReviewsService {
  constructor(
    private store: Store<StateWithReviews>,
    private userAgentService: UserAgentService,
  ) {}

  loadReviews(productCode: string): void {
    this.store.dispatch(new ReviewsActions.LoadReviews(productCode));
  }

  getReviews(productCode: string): Observable<StateUtils.LoaderState<ReviewsInfo>> {
    return this.store.pipe(
      select(ReviewsSelectors.getReviews(productCode)),
      tap((reviewState?: StateUtils.LoaderState<ReviewsInfo>) => {
        const isNotLoaded = !reviewState.value && !reviewState.loading && !reviewState.error && !reviewState.success;
        if (isNotLoaded && this.userAgentService.isSsrBotOrBrowser) {
          this.loadReviews(productCode);
        }
      }),
    );
  }

  getReviewsLoading(productCode: string): Observable<boolean> {
    return this.store.pipe(select(ReviewsSelectors.getReviewsLoading(productCode)));
  }

  /**
   * Возвращает рейтин и количество отзывов для товара,
   */
  getRating(productCode: string): Observable<ProductRating | null> {
    return this.getReviews(productCode).pipe(
      map((state: StateUtils.LoaderState<ReviewsInfo>) => {
        const isLoaded = state.error || state.success;

        if (isLoaded) {
          return {
            score: state.value ? state.value.stats.ratesTotalSum / state.value.stats.reviewsTotalCount : 0,
            reviewsCount: state.value ? state.value.stats.reviewsTotalCount : 0,
          };
        }

        return null;
      }),
    );
  }

  getAverageRating(productCode: string): Observable<number> {
    return this.getRating(productCode).pipe(map((rating) => rating?.score || 0));
  }

  getTotalCount(productCode: string): Observable<number> {
    return this.getRating(productCode).pipe(map((rating) => rating?.reviewsCount || 0));
  }

  getAddReviewLoading(): Observable<boolean> {
    return this.store.pipe(select(ReviewsSelectors.getAddReviewLoading));
  }

  getAddReviewSuccess(): Observable<boolean> {
    return this.store.pipe(select(ReviewsSelectors.getAddReviewSuccess));
  }

  getAddReviewError(): Observable<boolean> {
    return this.store.pipe(select(ReviewsSelectors.getAddReviewError));
  }

  addReview(params: AddReviewParams): void {
    this.store.dispatch(new ReviewsActions.AddReview(params));
  }

  resetAddReviewForm(): void {
    this.store.dispatch(new ReviewsActions.AddReviewReset());
  }
}
